import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import { IdNameList } from "../../../api/services/common/models";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiDoubleLabel,
  MuiGrid,
  MuiMultiSelect,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTypography,
  OptionsDataType,
} from "../../../components";
import MuiChipWithPopover from "../../../components/chipWithPopover/MuiChipWithPopover";
import {
  CurrencyFormat,
  valueWithoutFormatInFloat,
} from "../../../components/textFieldCurrency/CurrencyUtils";
import { TextFieldCurrency } from "../../../components/textFieldCurrency/TextFieldCurrency";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  MAX_MILESTONES_LIMIT,
} from "../../../helpers/constants";
import { COMPONENT_IDS } from "../../../helpers/constants/componentIds";
import { commonSiteCoreKeys } from "../../../helpers/constants/siteCoreKeys";
import {
  getAmountWithFormatByCurrencyName,
  getBillingScheduleExecutionStatus,
  getFormattedAmount,
  getSeriesIndexFromScheduleMilestone,
  isNotEmpty,
  isStatusExecuted,
  replaceHashWithValue,
  setApiDataToDropDownOption,
} from "../../../helpers/utils";
import { useGridContainer } from "../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { AmountBadgeText } from "../../../styles/common.style";
import { STATUS_BADGE_TYPE, StatusBadge } from "../../statusBadge/StatusBadge";
import { BillingConfigurationDetailsHeader } from "../billingConfigurationDetailsHeader/BillingConfigurationDetailsHeader";
import { BillingConfigurationHeader } from "../billingConfigurationHeader/BillingConfigurationHeader";
import {
  BillingConfigurationDetailsFormKeyProps,
  BillingTypeMilestoneConfigureTableData,
  BillingTypeScheduleConfigureTableData,
} from "../modelsAndSchemas/Models";
import { BillingConfigurationMilestoneTypeWrapper } from "./BillingConfigurationMilestoneType.style";

export interface BillingConfigurationMilestoneTypeProps {
  pageId: string;
  currencyName: string;
  currencyFormat: CurrencyFormat;
  currencyId: string;
  addBillingConfigurationSchedule: () => void;
  onFocusAmountHandled: () => void;
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[];
  onDeleteBillingConfigurationMilestoneTableEntryAction: (
    row: BillingTypeMilestoneConfigureTableData,
  ) => void;
  onDataAddedCallback: boolean;
  topicsList?: IdNameList[];
  onDataDeletedCallback: boolean;
  updateBillingConfigurationMilestone: () => boolean;
  onEditMilestoneEntry: (
    selectedMilestoneEntryForEdit: BillingTypeMilestoneConfigureTableData | null,
  ) => void;
  onEditCancel: () => void;
  billingConfigurationDetailsFormKeys: BillingConfigurationDetailsFormKeyProps;
  forceUpdate: () => void;
  isFromBillingInvoice?: boolean;
  isViewOnly?: boolean;
}

export const BillingConfigurationMilestoneType: React.FC<
  BillingConfigurationMilestoneTypeProps
> = ({
  pageId,
  currencyFormat,
  currencyId,
  currencyName,
  addBillingConfigurationSchedule,
  onFocusAmountHandled,
  filteredResults,
  onDeleteBillingConfigurationMilestoneTableEntryAction,
  onDataAddedCallback,
  topicsList,
  onDataDeletedCallback,
  updateBillingConfigurationMilestone,
  onEditMilestoneEntry,
  onEditCancel,
  billingConfigurationDetailsFormKeys,
  forceUpdate,
  isFromBillingInvoice,
  isViewOnly = false,
}) => {
  const { gridSizeMedium: gridSize } = useGridContainer();
  const { control, getValues, setValue, clearErrors } = useFormContext();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const {
    commonLabels,
    fetchValidationMessage,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
    commonBillingConfigurationDetails,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const activitiesDropdownRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);

  const {
    activities,
    billingConfigurationAmount,
    feePercentage,
    milestone,
    addMilestoneSubHeading: addMilestone,
    editMilestoneSubHeading: editMilestone,
    milestoneNumber,
    noMilestonesFoundMessage,
    totalAmount,
    totalPercentage,
    amountTableColumnHeading,
    percentageTableColumnHeading,
    milestoneTableColumnHeading,
    activitiesTableColumnHeading,
    actionTableColumnHeading: milestoneActionTableColumnHeading,
    value: milestoneFeePaymentTypeLabel,
    configurationDetailsMilestoneBasedHeading,
    statusTableColumnHeading: milestoneStatusTableColumnHeading,
  } = commonBillingConfigurationDetails;
  const [milestoneDropdownData, setMilestoneDropdownData] = useState<
    OptionsDataType[]
  >([]);

  const defaultRawMilestoneData = Object.fromEntries(
    Array.from({ length: MAX_MILESTONES_LIMIT }, (_, i) => [i + 1, ""]),
  );

  const rawDataMilestoneEntries = useRef<Record<number, string>>(
    defaultRawMilestoneData,
  );
  const [selectedMilestoneEntryForEdit, setSelectedMilestoneEntryForEdit] =
    useState<BillingTypeMilestoneConfigureTableData | null>(null);

  const [selectedTopicsForEdit, setSelectedTopicsForEdit] = useState<string[]>(
    [],
  );

  const [resetTopicsSelection, setResetTopicsSelection] =
    useState<boolean>(false);

  const [topicsDropdownData, setTopicsDropdownData] = useState<
    OptionsDataType[]
  >([]);

  const {
    billingTypeIdKey,
    feePercentageKey,
    amountWithFormatKey,
    amountKey,
    milestoneKey,
    activitiesKey,
    feePaymentTypeKey: milestoneFeePaymentTypeKey,
    statusKey: milestoneStatusKey = "",
    billingSchedulesKey,
  } = billingConfigurationDetailsFormKeys;

  const billingTypeIdValue = getValues(billingTypeIdKey);
  const milestoneFeePaymentTypeValue = getValues(milestoneFeePaymentTypeKey);
  const billingSchedules = getValues(billingSchedulesKey);

  const isAllSchedulesExecuted = useMemo(
    () => getBillingScheduleExecutionStatus(billingSchedules).allExecuted,
    [billingSchedules],
  );

  const setNewValueMilestone = () => {
    if (isNotEmpty(milestoneDropdownData)) {
      setValue(milestoneKey, milestoneDropdownData[0].value);
    }
  };

  const checkMilestoneDataEntries = () => {
    rawDataMilestoneEntries.current = defaultRawMilestoneData;

    filteredResults.forEach((item) => {
      const addMilestoneNumber = getSeriesIndexFromScheduleMilestone(
        item.description,
      );
      rawDataMilestoneEntries.current[addMilestoneNumber] =
        item?.topicIds?.toString();
    });
  };

  const updateMilestoneDataEntriesInEdit = (
    row: BillingTypeMilestoneConfigureTableData,
  ) => {
    const addMilestoneNumber = getSeriesIndexFromScheduleMilestone(
      row?.description ?? "",
    );
    rawDataMilestoneEntries.current[addMilestoneNumber] = "";
  };

  const resetMilestoneDataEntriesInEdit = (
    row?: BillingTypeMilestoneConfigureTableData | null,
  ) => {
    if (row) {
      const addMilestoneNumber = getSeriesIndexFromScheduleMilestone(
        row?.description ?? "",
      );
      rawDataMilestoneEntries.current[addMilestoneNumber] =
        row.topics?.toString() ?? "";
      updateAllMilestoneConfig();
    }
  };

  const updateAllMilestoneConfig = () => {
    checkMilestoneDataEntries();
    updateMilestoneDropdownData();
    updateTopicsDropdownData();
  };

  useEffect(() => {
    updateAllMilestoneConfig();
  }, [onDataAddedCallback, onDataDeletedCallback, filteredResults]);

  const updateMilestoneDropdownData = () => {
    const remainingMilestoneIndexes = Object.keys(
      rawDataMilestoneEntries.current,
    )
      .filter((key) => !rawDataMilestoneEntries.current[Number(key)])
      .map(Number);

    const milestoneOptionData: OptionsDataType[] =
      remainingMilestoneIndexes.map((index) => {
        const labelValue = replaceHashWithValue(milestoneNumber, {
          NUMBER: `${index}`,
        });
        return { label: labelValue, value: labelValue };
      });

    setMilestoneDropdownData(milestoneOptionData);
  };

  useEffect(() => {
    updateTopicsDropdownData();
  }, [selectedMilestoneEntryForEdit, topicsList]);

  const updateTopicsDropdownData = () => {
    const filteredResultsCopy = [...filteredResults];

    if (selectedMilestoneEntryForEdit) {
      const milestoneIndex = filteredResults.findIndex(
        (item) =>
          item.description === selectedMilestoneEntryForEdit?.description,
      );
      filteredResultsCopy.splice(milestoneIndex, 1);
    }
    const uniqueTopics = (topicsList ?? []).filter((topic) => {
      return !filteredResultsCopy.some((result) =>
        result.topicIds?.includes(topic.id),
      );
    });

    setTopicsDropdownData(setApiDataToDropDownOption(uniqueTopics ?? []));
  };

  useEffect(() => {
    setValue(feePercentageKey, "");
    setValue(amountWithFormatKey, "");
  }, [milestoneFeePaymentTypeValue]);

  useEffect(() => {
    activitiesDropdownRef.current?.resetValues();
  }, [onDataAddedCallback]);

  useEffect(() => {
    setNewValueMilestone();
  }, [milestoneDropdownData]);

  const isValueTypePercentage = () => {
    const feePaymentTypeValue = getValues(milestoneFeePaymentTypeKey);
    return (
      feePaymentTypeValue === BILLING_DETAILS_SITE_CORE_VALUE.valuePercentage
    );
  };

  const getMilestoneTotalAmountOrPercentage = () => {
    let totalAmountOrPercentage = 0;
    filteredResults.forEach((item) => {
      totalAmountOrPercentage += isValueTypePercentage()
        ? Number(item.percentage)
        : valueWithoutFormatInFloat(item.amountWithFormat);
    });
    return isValueTypePercentage()
      ? totalAmountOrPercentage
      : getFormattedAmount(totalAmountOrPercentage, currencyFormat?.locale);
  };

  const reachedMaxMilestoneLimit = () => {
    return (
      filteredResults.length === MAX_MILESTONES_LIMIT || isAllSchedulesExecuted
    );
  };

  const resetMilestoneValueOnEditFlowCancel = () => {
    setSelectedMilestoneEntryForEdit(null);
    setSelectedTopicsForEdit([]);
    setResetTopicsSelection(!resetTopicsSelection);
    setValue(feePercentageKey, "");
    setValue(amountKey, "");
    setValue(amountWithFormatKey, "");
    setValue(activitiesKey, "");
    updateTopicsDropdownData();
  };

  const clearMilestoneErrorOnEditFlow = () => {
    clearErrors(milestoneKey);
    clearErrors(feePercentageKey);
    clearErrors(amountWithFormatKey);
    clearErrors(activitiesKey);
  };

  const handleMilestoneEdit = (row: BillingTypeMilestoneConfigureTableData) => {
    clearMilestoneErrorOnEditFlow();
    setSelectedMilestoneEntryForEdit(row);
    updateMilestoneDataEntriesInEdit(row);
    updateMilestoneDropdownData();

    onEditMilestoneEntry(row);
    const amountWithFormatMilestone = getAmountWithFormatByCurrencyName(
      Number(row?.amount),
      currencyName,
    );
    setTimeout(() => {
      setValue(milestoneKey, row.description);
      setValue(feePercentageKey, row?.percentage);
      setValue(amountKey, row?.amount);
      setValue(amountWithFormatKey, amountWithFormatMilestone);
      setValue(activitiesKey, row?.topicIds);
      setSelectedTopicsForEdit(row.topicIds);
    }, 500);
  };

  const handleMilestoneEditCancelClick = () => {
    resetMilestoneDataEntriesInEdit(selectedMilestoneEntryForEdit);
    resetMilestoneValueOnEditFlowCancel();
    clearMilestoneErrorOnEditFlow();
    updateTopicsDropdownData();
    onEditCancel();
  };

  const handleMilestoneUpdateClick = () => {
    if (updateBillingConfigurationMilestone()) {
      setSelectedMilestoneEntryForEdit(null);
      setSelectedTopicsForEdit([]);
      setResetTopicsSelection(!resetTopicsSelection);
    }
  };

  const getPaymentTypeValue = () => {
    const feePaymentTypeValue = getValues(milestoneFeePaymentTypeKey);
    return fetchValueByKeyOfDropdown(
      commonSiteCoreKeys.billingConfigurationDetails,
      commonSiteCoreKeys.valueList,
      feePaymentTypeValue,
    );
  };

  const columns: Column[] = [
    {
      id: milestoneKey,
      label: milestoneTableColumnHeading,
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{row.description}</MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: isValueTypePercentage() ? feePercentageKey : amountWithFormatKey,
      label: isValueTypePercentage()
        ? percentageTableColumnHeading
        : amountTableColumnHeading,

      isSortVisible: false,
      render: (row: any) => {
        const amountWithFormatMilestone = getAmountWithFormatByCurrencyName(
          Number(row?.amount),
          currencyName,
        );
        return (
          <MuiBox>
            <MuiTypography variant="h3">
              {isValueTypePercentage()
                ? `${row.percentage}%`
                : `${currencyName} ${amountWithFormatMilestone}`}
            </MuiTypography>
          </MuiBox>
        );
      },
    },
    {
      id: activitiesKey,
      label: activitiesTableColumnHeading,
      isSortVisible: false,
      render: (row: any) => {
        return (
          <MuiBox>
            <MuiChipWithPopover
              data={row.topicName}
              handleDelete={() => {}}
              id={"selected-topics"}
              isEditable={false}
              headerLabel={""}
              gridRef={React.createRef<HTMLElement>()}
              countToWrap={1}
              showMoreARIALabel={commonARIALabel.showMoreDetails}
              removeChipNameARIALabel={commonARIALabel.removeChipName}
              closePopoverARIALabel={commonARIALabel.closePopover}
            />
            <MuiTypography variant="h3"></MuiTypography>
          </MuiBox>
        );
      },
    },
    ...(isFromBillingInvoice
      ? [
          {
            id: milestoneStatusKey,
            label: milestoneStatusTableColumnHeading,
            isSortVisible: false,
            render: (row: any) => {
              const milestoneStatus = isNotEmpty(row.statusId)
                ? row.statusId
                : STATUS_BADGE_TYPE.scheduled;
              return (
                <StatusBadge
                  status={milestoneStatus}
                  label={fetchValueByKeyOfDropdown(
                    commonSiteCoreKeys.billingConfigurationDetails,
                    commonSiteCoreKeys.billingStatusList,
                    milestoneStatus,
                  )}
                />
              );
            },
          },
        ]
      : []),
    ...(isViewOnly
      ? []
      : [
          {
            id: "action",
            label: milestoneActionTableColumnHeading,
            render: (row: any) => {
              if (
                row.description === selectedMilestoneEntryForEdit?.description
              )
                return null;

              if (isFromBillingInvoice && isStatusExecuted(row.statusId)) {
                return null;
              }

              const lastMilestoneRecord =
                filteredResults[filteredResults.length - 1].description;

              const menuItems = [
                {
                  onClick: () => handleMilestoneEdit(row),
                  dataTestId: `edit-milestone-btn-${row.id}`,
                  label: commonLabels.edit,
                },
              ];
              if (row.description === lastMilestoneRecord) {
                menuItems.push({
                  onClick: () =>
                    onDeleteBillingConfigurationMilestoneTableEntryAction(row),
                  dataTestId: `delete-milestone-action`,
                  label: commonLabels.delete,
                });
              }
              return <MuiOptionMenu menuItems={menuItems} />;
            },
          },
        ]),
  ];
  const showComponent =
    billingTypeIdValue === BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone;
  if (!showComponent) return null;

  const renderTotalPercentageWithTable = () => {
    type BillingTypeScheduleConfigureTableDataKeys =
      keyof BillingTypeScheduleConfigureTableData;
    const descriptionRowKey: BillingTypeScheduleConfigureTableDataKeys =
      "description";
    return (
      <>
        <MuiBox className="billing-configuration-total-amount-percentage-container">
          <MuiTypography variant="h3Bold">
            {isValueTypePercentage() ? totalPercentage : totalAmount}
          </MuiTypography>
          <AmountBadgeText variant="h3Bold">
            {isValueTypePercentage()
              ? `${getMilestoneTotalAmountOrPercentage()}%`
              : `${currencyName} ${getMilestoneTotalAmountOrPercentage()}`}
          </AmountBadgeText>
        </MuiBox>
        <MuiBox className="billing-configuration-table">
          <MuiTable
            pageId={pageId}
            tableHeaderForm={[]}
            columns={columns}
            isFooterVisible={false}
            tableResults={[...filteredResults]}
            noDataInApi={false}
            noResultMessage={noMilestonesFoundMessage}
            priorityResults={[]}
            selectedId={selectedMilestoneEntryForEdit?.description}
            selectedIdKey={descriptionRowKey}
          />
        </MuiBox>
      </>
    );
  };

  const renderViewOnlyLabel = () => {
    return (
      <MuiDoubleLabel
        label={milestoneFeePaymentTypeLabel}
        replaceEmptyWithDash
        value={getPaymentTypeValue()}
      />
    );
  };

  const renderDropdownField = () => {
    return (
      <FormController
        key={milestoneFeePaymentTypeValue}
        control={control}
        name={milestoneFeePaymentTypeKey}
      >
        <MuiSelect
          data-testid="test-page-milestoneFeePaymentType"
          label={milestoneFeePaymentTypeLabel}
          options={fetchDropdownValues(
            commonSiteCoreKeys.billingConfigurationDetails,
            commonSiteCoreKeys.valueList,
          )}
          disabled={filteredResults.length > 0}
          onChange={forceUpdate}
          defaultValue={""}
          visibleDefaultSelectOption={true}
          isMandatory
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    );
  };

  return (
    <BillingConfigurationMilestoneTypeWrapper>
      <MuiBox className="dashed-line" data-testid="dashed-line" />
      <MuiBox className="config-header">
        <BillingConfigurationDetailsHeader
          header={configurationDetailsMilestoneBasedHeading}
          showBlueBackground={!isFromBillingInvoice}
        />
      </MuiBox>
      <MuiBox className={"billing-configuration-top"}>
        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSize}>
            {isViewOnly ? renderViewOnlyLabel() : renderDropdownField()}
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      {isViewOnly && renderTotalPercentageWithTable()}
      {!isViewOnly && milestoneFeePaymentTypeValue ? (
        <>
          <BillingConfigurationHeader
            header={
              selectedMilestoneEntryForEdit ? editMilestone : addMilestone
            }
          />
          <MuiBox className="billing-configuration-middle">
            <MuiGrid container spacing={2}>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={milestoneKey}
                  data-testid="test-page-billingConfigurationAmount"
                >
                  <MuiSelect
                    data-testId={`${pageId}-${milestoneKey}`}
                    label={milestone}
                    options={milestoneDropdownData}
                    visibleDefaultSelectOption={true}
                    isMandatory
                    placeholder={commonLabels.selectPlaceHolder}
                    IconComponent={undefined}
                    disabled
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
              {milestoneFeePaymentTypeValue ===
              BILLING_DETAILS_SITE_CORE_VALUE.valuePercentage ? (
                <MuiGrid size={gridSize}>
                  <FormController control={control} name={feePercentageKey}>
                    <TextFieldCurrency
                      isMandatory
                      id={`${pageId}-${feePercentageKey}`}
                      formId={feePercentageKey}
                      locale={currencyFormat?.locale}
                      useGrouping={false}
                      isCurrencyChanged={currencyId}
                      zeroValidationMessage={fetchValidationMessage(
                        commonSiteCoreKeys.greaterThanZeroFieldValidator,
                      )}
                      formatErrorMessage={fetchValidationMessage(
                        commonSiteCoreKeys.alphabetNotAllowedValidator,
                      )}
                      name={feePercentageKey}
                      label={feePercentage}
                      placeholder={commonLabels.inputPlaceHolder}
                      getFinalValues={(value, _valueNonFormatted) => {
                        setValue(feePercentageKey, value);
                      }}
                      percentRangeErrorMessage={fetchValidationMessage(
                        commonSiteCoreKeys.pleaseEnterNumberBetween1To100,
                      )}
                      allowDecimal={false}
                      disabled={isAllSchedulesExecuted}
                      isForPercentageUsage
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
              ) : (
                <MuiGrid size={gridSize}>
                  <FormController control={control} name={amountWithFormatKey}>
                    <TextFieldCurrency
                      isMandatory
                      id={`${pageId}-${amountWithFormatKey}`}
                      formId={amountWithFormatKey}
                      locale={currencyFormat?.locale}
                      isCurrencyChanged={currencyId}
                      disabled={isAllSchedulesExecuted}
                      zeroValidationMessage={fetchValidationMessage(
                        commonSiteCoreKeys.greaterThanZeroFieldValidator,
                      )}
                      formatErrorMessage={fetchValidationMessage(
                        commonSiteCoreKeys.alphabetNotAllowedValidator,
                      )}
                      name={amountWithFormatKey}
                      label={`${billingConfigurationAmount} (${currencyName})`}
                      placeholder={commonLabels.inputPlaceHolder}
                      getFinalValues={(value, valueNonFormatted) => {
                        setValue(amountKey, valueNonFormatted);
                        setValue(amountWithFormatKey, value);
                      }}
                      onFocusCalled={onFocusAmountHandled}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
              )}
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={activitiesKey}
                  ref={activitiesDropdownRef}
                >
                  <MuiMultiSelect
                    data-testId={`${pageId}-${activitiesKey}`}
                    label={activities}
                    options={topicsDropdownData}
                    defaultValue={""}
                    disabled={isAllSchedulesExecuted}
                    isMandatory
                    placeholder={commonLabels.selectPlaceHolder}
                    showChip
                    showSearch
                    isChipEditable={!isAllSchedulesExecuted}
                    dialogTitle={commonLabels.selectedCountries}
                    selectedValues={selectedTopicsForEdit}
                    reset={resetTopicsSelection}
                    selectPlaceHolder={commonLabels.selectPlaceHolder}
                    searchPlaceHolder={commonLabels.search}
                    selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                    doneText={commonLabels.done}
                    allARIAProps={multiSelectARIALabels}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>

            <MuiBox className="billing-configuration-add-button">
              {selectedMilestoneEntryForEdit ? (
                <>
                  <MuiButtonSecondary
                    id={COMPONENT_IDS.CANCEL_BUTTON}
                    className="bottom-button-clear"
                    onClick={handleMilestoneEditCancelClick}
                  >
                    {commonLabels.cancel}
                  </MuiButtonSecondary>
                  <MuiButtonPrimary
                    id={COMPONENT_IDS.UPDATE_BUTTON}
                    className="bottom-button-continue"
                    onClick={handleMilestoneUpdateClick}
                  >
                    {commonLabels.update}
                  </MuiButtonPrimary>
                </>
              ) : (
                <MuiButtonSecondary
                  onClick={addBillingConfigurationSchedule}
                  data-testid="add-milestone-btn"
                  data-testId={COMPONENT_IDS.BILLING_CONFIGURATION_ADD_BUTTON}
                  disabled={reachedMaxMilestoneLimit()}
                >
                  {commonLabels.add}
                </MuiButtonSecondary>
              )}
            </MuiBox>
          </MuiBox>

          {renderTotalPercentageWithTable()}
        </>
      ) : null}
    </BillingConfigurationMilestoneTypeWrapper>
  );
};
